/**
 * Reading the moment, and finding the phrase already in progress.
 *
 * A verb file, beside `build.ts` and `explain.ts`: `patterns.ts` is the data and this is
 * what walks it. Nothing here draws an rng value, consults a clock, or looks at anything
 * other than the chords that were actually played.
 *
 * ## The five rules that keep a match honest
 *
 * 1. **Contiguous, and ending on the current chord.** The steps map onto the last
 *    `steps.length` entries of the tail, in order. A ii–V with something between them
 *    does not match. This is what makes the claim checkable: the chords a card names as
 *    the setup are the chords visible in the history rail, in that order.
 * 2. **Only what was played.** A pattern never consults `fromSuggestionId` and never asks
 *    which move authored a chord. A player who invents their own ii–V is in a ii–V, and a
 *    player who took one from a card is in exactly the same one. Rules 4 and 11.
 * 3. **A journey behind it.** `PATTERN_MIN_PATH`, applied globally rather than per
 *    pattern, which is what lets the one-step `standing-dominant` row exist without
 *    firing on the first chord of a session.
 * 4. **A stricter key gate than a move needs.** `PATTERN_KEY_CONFIDENCE` for anything
 *    anchored to the tonic, plus `needsKeyMode` where it is set.
 * 5. **One shape, where the pattern is about a shape.** `sameShape` makes the two
 *    chromatic lines require the whole matched tail to be one reading of a chord, because
 *    roots alone cannot tell a climb from a passing chord between two arrivals.
 *
 * ## Ordering
 *
 * Longer tail first; a key-anchored match breaks a tie with a root-anchored one; table
 * order breaks that. Specificity is the whole ranking policy and it needs no tuning —
 * with one pattern slot per set, "the most specific match that has an eligible
 * continuation" is a complete answer, and a `strength` number nobody could derive is a
 * number somebody would eventually tune.
 */

import { mod12 } from '../chord/enharmonic.js';
import type { PitchClass } from '../chord/types.js';
import { readCurrentQuality, readPathChord } from './build.js';
import {
  PATTERN_KEY_CONFIDENCE,
  PATTERN_LIBRARY,
  PATTERN_MIN_PATH,
  PATTERN_TAIL_LIMIT,
  QUALITY_MATCHES,
} from './patterns.js';
import type {
  CurrentQuality,
  HarmonicPattern,
  HeuristicBranchInput,
  HeuristicContext,
  PathChord,
  PatternMatch,
  PatternStep,
} from './types.js';

/**
 * Everything the engine knows about the moment, read once.
 *
 * The current chord joins the tail as its last entry, because a phrase includes the chord
 * you are standing on — every pattern in the table ends on it.
 */
export function readContext(input: HeuristicBranchInput): HeuristicContext {
  const currentQuality = readCurrentQuality(input.current);
  const path = (input.path ?? []).map(readPathChord);
  const current: PathChord = {
    symbol: input.current.symbol,
    pitchClasses: input.current.pitchClasses,
    rootPc: input.current.rootPc,
    bassPc: input.current.bassPc,
    quality: currentQuality,
  };

  return {
    current: input.current,
    currentQuality,
    keyEstimate: input.keyEstimate,
    adventurousness: input.adventurousness,
    path,
    tail: [...path, current].slice(-PATTERN_TAIL_LIMIT),
  };
}

function stepAccepts(step: PatternStep, quality: CurrentQuality): boolean {
  if (!step.quality) return true;
  for (const match of step.quality) {
    if ((QUALITY_MATCHES[match] as readonly CurrentQuality[]).includes(quality)) return true;
  }
  return false;
}

function anchorFor(pattern: HarmonicPattern, context: HeuristicContext): PitchClass | null {
  if (pattern.anchor === 'root') return context.current.rootPc;
  if (context.keyEstimate.confidence < PATTERN_KEY_CONFIDENCE) return null;
  if (pattern.needsKeyMode !== undefined && pattern.needsKeyMode !== context.keyEstimate.mode) {
    return null;
  }
  return context.keyEstimate.tonicPc;
}

function matchOne(pattern: HarmonicPattern, context: HeuristicContext): PatternMatch | null {
  if (pattern.steps.length > context.tail.length) return null;
  const anchor = anchorFor(pattern, context);
  if (anchor === null) return null;

  const slice = context.tail.slice(-pattern.steps.length);
  for (let i = 0; i < pattern.steps.length; i += 1) {
    const step = pattern.steps[i] as PatternStep;
    const chord = slice[i] as PathChord;
    if (mod12(chord.rootPc - anchor) !== step.semitones) return null;
    if (!stepAccepts(step, chord.quality)) return null;
    // A shape pattern is about one shape moved bodily, so a tail of three different
    // readings is three neighbouring chords rather than a gesture. This is what keeps the
    // two commonest passing figures in the language — the tritone-substitute cadence
    // Dm7–D♭7–C and the passing diminished C–C♯°–Dm — from being read as lines that want
    // carrying on past the arrival they actually are.
    if (pattern.sameShape === true && chord.quality !== (slice[0] as PathChord).quality) {
      return null;
    }
  }

  return { pattern, setup: slice.map((chord) => chord.symbol) };
}

/**
 * Every pattern the tail satisfies, most specific first.
 *
 * Returns `[]` below `PATTERN_MIN_PATH` — no allocation, no rng, nothing. That is the
 * matching half of the quiescence invariant.
 */
export function matchPatterns(context: HeuristicContext): PatternMatch[] {
  if (context.path.length < PATTERN_MIN_PATH) return [];

  const out: Array<{ match: PatternMatch; order: number }> = [];
  for (let order = 0; order < PATTERN_LIBRARY.length; order += 1) {
    const match = matchOne(PATTERN_LIBRARY[order] as HarmonicPattern, context);
    if (match) out.push({ match, order });
  }

  out.sort((a, b) => {
    const byLength = b.match.pattern.steps.length - a.match.pattern.steps.length;
    if (byLength !== 0) return byLength;
    const byAnchor =
      Number(b.match.pattern.anchor === 'key') - Number(a.match.pattern.anchor === 'key');
    if (byAnchor !== 0) return byAnchor;
    return a.order - b.order;
  });

  return out.map((entry) => entry.match);
}
